import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import almacuraImage from '../images/AlmacuraImage.png';
import mlcImage from '../images/MlcImage.png';
import tgmImage from '../images/TgmImage.png';
import otpImage from '../images/OTPImage.jpg';
import { FiExternalLink, FiGithub } from 'react-icons/fi';

// Styled Components
const ProjectContainer = styled.div`
  max-width: 1200px;
  margin: 80px auto;
  padding: 40px;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: 768px) {
    flex-direction: row;
    text-align: left;
    gap: 40px;
  }
`;

const ProjectImage = styled.img`
  width: 300px;
  height: auto;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const TitleSection = styled.div`
  flex: 1;
`;

const ProjectTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin: 0;
  font-weight: bold;
`;

const ProjectDescription = styled.p`
  font-size: 1.1rem;
  color: #555;
  line-height: 1.8;
  margin-top: 20px;
`;

const LinksSection = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 20px;
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: ${props => props.bgColor || '#007bff'};
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.hoverColor || '#0056b3'};
    transform: translateY(-3px);
  }
`;

const TechStack = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const TechBadge = styled(motion.span)`
  padding: 8px 14px;
  background-color: #e0e0e0;
  border-radius: 16px;
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
`;

const projects = [
  {
    id: '1',
    title: 'Olive Tree People',
    description:
    'An international wellness and skincare company specializing in natural olive-based products. Led development of multilingual, WCAG-compliant e-commerce features to support global expansion. Integrated Shopify with custom JavaScript/TypeScript modules for enhanced product pages, implemented real-time analytics tracking via Google Analytics, and optimized site performance for high-traffic campaigns.',
    liveUrl: 'https://us.olivetreepeople.com/',
    technologies: ['Shopify Plus', 'React', 'SQL'],
    image: otpImage,
  },
  {
    id: '2',
    title: 'Almacura',
    description:
      'A startup providing affordable, culturally attuned therapy services for US-based Latinos. This application utilizes a serverless architecture for quick data retrieval, leveraging Neon Console for database management, Google Cloud Console for hosting and video call functionality, and Sendbird for real-time chat integration.',
    liveUrl: 'https://therapy-main-90ffp0bs0-elva-osorios-projects.vercel.app/',
    githubUrl: 'https://github.com/elvaosorio/therapy-main',
    technologies: ['TypeScript', 'Javascript', 'APIs'],
    image: otpImage,
  },
  {
    id: '3',
    title: 'The Goddess Mercado',
    description:
      'A nonprofit e-commerce platform built on Shopify, designed to help Latino entrepreneurs showcase their businesses. The website features a custom, visually appealing design powered by CSS to enhance the user experience, allowing users to easily explore and apply for spots in the marketplace.',
    liveUrl: 'https://thegoddessmercado.com/',
    githubUrl: '',
    technologies: ['Shopify', 'CSS', 'HTML'],
    image: tgmImage,
  },
  {
    id: '4',
    title: 'Mariachi Los Criollos de Guadalajara',
    description:
      'Designed and customized a WordPress-based website with HTML and CSS enhancements to showcase mariachi performances, booking options, and media galleries. Focused on delivering a professional, responsive, and user-friendly experience for visitors.',
    liveUrl: 'https://mariachiloscriollosdeguadalajara.com/',
    githubUrl: '',
    technologies: ['WordPress', 'HTML', 'CSS'],
    image: mlcImage,
  },
];

function ProjectDetail() {
  const { id } = useParams();
  const project = projects.find(p => p.id === id);

  if (!project) return <div>Project not found</div>;

  return (
    <ProjectContainer>
      <HeaderSection>
        <ProjectImage src={project.image} alt={`${project.title} Thumbnail`} />
        <TitleSection>
          <ProjectTitle>{project.title}</ProjectTitle>
          <ProjectDescription>{project.description}</ProjectDescription>
          <LinksSection>
            <ProjectLink
              href={project.liveUrl}
              target="_blank"
              bgColor="#007bff"
              hoverColor="#0056b3"
            >
              <FiExternalLink /> Live Demo
            </ProjectLink>
            {project.githubUrl && (
              <ProjectLink
                href={project.githubUrl}
                target="_blank"
                bgColor="#333"
                hoverColor="#555"
              >
                <FiGithub /> GitHub
              </ProjectLink>
            )}
          </LinksSection>
        </TitleSection>
      </HeaderSection>
      <TechStack>
        {project.technologies.map(tech => (
          <TechBadge
            key={tech}
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.2 }}
          >
            {tech}
          </TechBadge>
        ))}
      </TechStack>
    </ProjectContainer>
  );
}

export default ProjectDetail;
